// Canonical founder + credential data. Single source of truth so the byline,
// homepage founder strip, /about page, Oracle credibility band, structured data,
// and llms.txt all stay consistent.
//
// Public-facing title is "Founder" (see About copy). "Founder & CEO" is reserved
// for legal/contractual contexts and email signatures, intentionally not used here.

export interface Credential {
  name: string;
  issuer: string;
  issued: string; // human-readable issue month/year
  verifyUrl: string; // Oracle CertView "Share Credential" link — issuer-verifiable
  image: string; // /certs/*.jpg
}

export const FOUNDER = {
  name: 'Andrew Blackman',
  role: 'Founder',
  company: 'EZ Cloud',
  yearsOracleAP: 25,
  yearsWebCenter: 20,
  photo: '/andrew-blackman.jpg',
  photoAlt: 'Andrew Blackman, Founder of EZ Cloud',
  credlyUrl: 'https://www.credly.com/users/andrewablackman_ezcloud_ai',
  linkedinUrl: 'https://www.linkedin.com/in/andrewablackman/',
  // One-liner for the blog author box.
  shortBio:
    'Andrew Blackman is the founder of EZ Cloud and a 25-year Oracle specialist with 20+ years specifically on Oracle WebCenter — Content (UCM), Imaging (IPM), Capture (ODC), Forms Recognition (WFR), SOA Suite, WebLogic, ADF. He writes about modernizing Oracle WebCenter AP and automating AP natively on Oracle EBS and Fusion Cloud.',
  // Fuller bio for the /about page and the homepage founder strip.
  bio:
    "Andrew Blackman is the founder of EZ Cloud. He has spent 25 years across the Oracle ecosystem and 20+ years specifically inside Oracle WebCenter — Content (UCM/Stellent), Imaging (IPM), Capture (ODC), Forms Recognition (WFR), SOA Suite, WebLogic Server (11g, 12c, 14c), ADF customizations, BPM, and Managed Attachments for E-Business Suite. His WebCenter consulting career has included Fortune 500 engagements across financial services (Equifax, Marsh & McLennan, Experian, Lincoln Financial Group), healthcare (Blue Cross Blue Shield, Harvard Pilgrim Healthcare), government (US House of Representatives, US Department of Agriculture, State of Alaska), industrial (Archer Daniels Midland, ASML), transportation (Metropolitan Transportation Authority of New York, Via Rail), and technology (Wipro / AboveNet, Singapore Stock Exchange). Most recent engagement was an Oracle Consulting Services contract at Via Rail delivering WebCenter Forms Recognition AP automation on Oracle Cloud Infrastructure integrated with Oracle EBS R12 and Fusion Cloud ERP. He founded EZ Cloud on a simple conviction: invoice capture is now commoditized, but automation that genuinely respects how Oracle AP runs — the open interfaces, the multi-fund coding, the period-close edge cases, and the WebCenter heritage many enterprises still depend on — is not. EZ Cloud is published on the Oracle Reference Architecture for OCI and featured in Oracle's own technical case studies.",
} as const;

export const CERTIFICATIONS: Credential[] = [
  {
    name: 'Oracle Cloud Infrastructure 2025 Certified AI Foundations Associate',
    issuer: 'Oracle',
    issued: 'August 2025',
    verifyUrl:
      'https://catalog-education.oracle.com/pls/certview/sharebadge?id=8921EECDEE4B7CE043C23E0CBD64645ED65E07E141E94B07F407EB073CC688CF',
    image: '/certs/oci-2025-ai-foundations-associate.jpg',
  },
  {
    name: 'Oracle Cloud Infrastructure 2024 Generative AI Certified Professional',
    issuer: 'Oracle',
    issued: 'July 2024',
    verifyUrl:
      'https://catalog-education.oracle.com/pls/certview/sharebadge?id=2DB1E7CDBA8381B99F459AA34C32A6A2886DFF7760B3DF3B711A8BB072D7C581',
    image: '/certs/oci-2024-generative-ai-professional.jpg',
  },
];

export const FOUNDER_PERSON_ID = 'https://ezcloud.ai/about#andrew-blackman';

// JSON-LD Person node — referenced by @id from the Organization and Article schemas.
export const founderPersonLd = {
  '@type': 'Person',
  '@id': FOUNDER_PERSON_ID,
  name: FOUNDER.name,
  jobTitle: FOUNDER.role,
  description: FOUNDER.shortBio,
  image: 'https://ezcloud.ai/andrew-blackman.jpg',
  url: 'https://ezcloud.ai/about',
  worksFor: { '@id': 'https://ezcloud.ai/#organization' },
  sameAs: [FOUNDER.credlyUrl, FOUNDER.linkedinUrl],
  // Explicit expertise list reinforces Knowledge Graph entity recognition.
  knowsAbout: [
    'Oracle WebCenter Content',
    'Oracle WebCenter Imaging',
    'Oracle WebCenter Forms Recognition',
    'Oracle WebCenter Enterprise Capture',
    'Oracle WebCenter Portal',
    'Oracle SOA Suite',
    'Oracle WebLogic Server',
    'Oracle ADF',
    'Oracle BPM',
    'Managed Attachments for Oracle E-Business Suite',
    'Oracle E-Business Suite Payables',
    'Oracle Fusion Cloud ERP',
    'Oracle Integration Cloud',
    'Oracle Cloud Infrastructure',
    'FIPSA',
    'Accounts Payable Automation',
  ],
  alumniOf: {
    '@type': 'CollegeOrUniversity',
    name: 'Farnborough College of Technology',
    address: { '@type': 'PostalAddress', addressCountry: 'GB' },
  },
  memberOf: [
    {
      '@type': 'Organization',
      name: 'Independent Oracle Users Group (IOUG)',
    },
  ],
  hasCredential: CERTIFICATIONS.map((c) => ({
    '@type': 'EducationalOccupationalCredential',
    credentialCategory: 'certification',
    name: c.name,
    url: c.verifyUrl,
    recognizedBy: { '@type': 'Organization', name: c.issuer },
  })),
};
